<template>
  <div class="regra-condicional">
    <div class="header-info">
      <fa-icon icon="info-circle" />
      As regras são avaliadas em ordem; a primeira que casar define o valor enviado.
    </div>

    <div v-for="(caso, ci) in regra.casos" :key="`caso-${ci}`" class="regra-bloco">
      <div class="regra-header">
        <span class="regra-titulo">Regra {{ ci + 1 }}</span>
        <button
          type="button"
          class="btn-modo-link"
          v-if="!isAvancado(ci)"
          @click="ativarAvancado(ci)"
        >
          <fa-icon icon="cogs" /> Configurar lógica avançada (E/OU aninhados)
        </button>
        <button
          type="button"
          class="btn-modo-link"
          v-else
          @click="desativarAvancado(ci)"
        >
          <fa-icon icon="undo" /> Voltar ao modo simples
        </button>
        <button
          type="button"
          class="btn-mini btn-mini--danger"
          v-if="regra.casos.length > 1"
          @click="removerCaso(ci)"
          title="Remover regra"
        >
          <fa-icon icon="trash-alt" />
        </button>
      </div>

      <div class="quando-bloco">
        <div class="bloco-label">Quando</div>

        <!-- Modo simples: AND linear de condições -->
        <div v-if="!isAvancado(ci)" class="cond-lista">
          <div
            v-for="(cond, ki) in caso.condicao.grupos[0].condicoes"
            :key="`cond-${ci}-${ki}`"
            class="cond-linha"
          >
            <span class="cond-conector">{{ ki === 0 ? '' : 'E' }}</span>
            <select class="form-control form-control-sm cond-campo" v-model="cond.campo">
              <option value="">Campo</option>
              <option v-for="c in campos" :key="c.value" :value="c.value">{{ c.label }}</option>
            </select>
            <select class="form-control form-control-sm cond-op" v-model="cond.operador">
              <option v-for="op in operadores" :key="op.value" :value="op.value">{{ op.label }}</option>
            </select>
            <input
              v-if="!operadorSemValor(cond.operador)"
              class="form-control form-control-sm cond-valor"
              type="text"
              v-model="cond.valor"
              placeholder="Valor"
            />
            <button
              type="button"
              class="btn-mini btn-mini--danger"
              v-if="caso.condicao.grupos[0].condicoes.length > 1"
              @click="removerCondicaoSimples(ci, ki)"
              title="Remover condição"
            >
              <fa-icon icon="times" />
            </button>
          </div>
          <button type="button" class="btn-add-inline" @click="adicionarCondicaoSimples(ci)">
            <fa-icon icon="plus" /> Adicionar condição (E)
          </button>
        </div>

        <!-- Modo avançado: grupos com AND/OR aninhados -->
        <div v-else class="modo-avancado">
          <div
            v-for="(grupo, gi) in caso.condicao.grupos"
            :key="`grupo-${ci}-${gi}`"
            class="grupo-bloco"
          >
            <div class="grupo-header">
              <span class="grupo-prefix">{{ gi === 0 ? 'Atende' : 'E também atende' }}</span>
              <select
                class="form-control form-control-sm logica-select"
                :value="grupo.logica"
                @change="alterarLogica(ci, gi, $event.target.value)"
              >
                <option value="AND">todas as condições</option>
                <option value="OR">qualquer uma das condições</option>
              </select>
              <button
                type="button"
                class="btn-mini btn-mini--danger"
                v-if="caso.condicao.grupos.length > 1"
                @click="removerGrupo(ci, gi)"
                title="Remover grupo"
              >
                <fa-icon icon="times" />
              </button>
            </div>

            <div
              v-for="(cond, ki) in grupo.condicoes"
              :key="`cond-${ci}-${gi}-${ki}`"
              class="cond-linha"
            >
              <span class="cond-conector">
                {{ ki === 0 ? '' : (grupo.logica === 'AND' ? 'E' : 'OU') }}
              </span>
              <select class="form-control form-control-sm cond-campo" v-model="cond.campo">
                <option value="">Campo</option>
                <option v-for="c in campos" :key="c.value" :value="c.value">{{ c.label }}</option>
              </select>
              <select class="form-control form-control-sm cond-op" v-model="cond.operador">
                <option v-for="op in operadores" :key="op.value" :value="op.value">{{ op.label }}</option>
              </select>
              <input
                v-if="!operadorSemValor(cond.operador)"
                class="form-control form-control-sm cond-valor"
                type="text"
                v-model="cond.valor"
                placeholder="Valor"
              />
              <button
                type="button"
                class="btn-mini btn-mini--danger"
                v-if="grupo.condicoes.length > 1"
                @click="removerCondicao(ci, gi, ki)"
                title="Remover condição"
              >
                <fa-icon icon="times" />
              </button>
            </div>

            <button type="button" class="btn-add-inline" @click="adicionarCondicao(ci, gi)">
              <fa-icon icon="plus" /> Adicionar condição
            </button>
          </div>

          <button type="button" class="btn-add-inline" @click="adicionarGrupo(ci)">
            <fa-icon icon="plus" /> Adicionar outro grupo (E)
          </button>
        </div>
      </div>

      <div class="enviar-bloco">
        <div class="bloco-label">Enviar</div>
        <ExpressaoValor
          :value="caso.entao"
          :campos="campos"
          @input="atualizarEntao(ci, $event)"
        />
      </div>
    </div>

    <button type="button" class="btn-add" @click="adicionarCaso">
      <fa-icon icon="plus" /> Adicionar regra
    </button>

    <div class="fallback-bloco">
      <div class="fallback-label">Quando nenhuma regra casar, enviar</div>
      <ExpressaoValor
        :value="regra.senao"
        :campos="campos"
        @input="atualizarSenao"
      />
    </div>
  </div>
</template>

<script>
import ExpressaoValor from "./ExpressaoValor";

const novaCondicao = () => ({
  campo: "",
  operador: "igual",
  valor: "",
  tipo_valor: "static",
});

const novoGrupo = () => ({
  logica: "AND",
  condicoes: [novaCondicao()],
});

const novoCaso = () => ({
  condicao: { grupos: [novoGrupo()] },
  entao: { tipo: "literal", valor: "" },
});

export const regraCondicionalVazia = () => ({
  tipo: "condicional",
  casos: [novoCaso()],
  senao: { tipo: "literal", valor: "" },
});

export default {
  name: "RegraCondicional",
  components: { ExpressaoValor },
  props: {
    value: { type: Object, required: true },
    campos: { type: Array, default: () => [] },
    operadores: { type: Array, default: () => [] },
  },
  data() {
    return {
      avancadoPorCaso: {},
    };
  },
  computed: {
    regra() {
      return this.value;
    },
  },
  methods: {
    operadorSemValor(op) {
      return op === "nulo" || op === "nao_nulo";
    },
    isAvancado(ci) {
      // avançado: forçado pelo usuário OU já tem mais de 1 grupo OU algum grupo é OR
      if (this.avancadoPorCaso[ci]) return true;
      const caso = this.regra.casos[ci];
      if (!caso) return false;
      if (caso.condicao.grupos.length > 1) return true;
      if (caso.condicao.grupos[0] && caso.condicao.grupos[0].logica === "OR") return true;
      return false;
    },
    ativarAvancado(ci) {
      this.$set(this.avancadoPorCaso, ci, true);
    },
    desativarAvancado(ci) {
      // simplifica: mantém apenas o primeiro grupo, força AND
      const caso = this.regra.casos[ci];
      if (caso.condicao.grupos.length > 1) {
        caso.condicao.grupos = [caso.condicao.grupos[0]];
      }
      caso.condicao.grupos[0].logica = "AND";
      this.$set(this.avancadoPorCaso, ci, false);
      this.emit();
    },
    emit() {
      this.$emit("input", { ...this.regra });
    },
    adicionarCaso() {
      this.regra.casos.push(novoCaso());
      this.emit();
    },
    removerCaso(ci) {
      this.regra.casos.splice(ci, 1);
      this.$delete(this.avancadoPorCaso, ci);
      this.emit();
    },
    adicionarGrupo(ci) {
      this.regra.casos[ci].condicao.grupos.push(novoGrupo());
      this.emit();
    },
    removerGrupo(ci, gi) {
      this.regra.casos[ci].condicao.grupos.splice(gi, 1);
      this.emit();
    },
    alterarLogica(ci, gi, valor) {
      this.$set(this.regra.casos[ci].condicao.grupos[gi], "logica", valor);
      this.emit();
    },
    adicionarCondicao(ci, gi) {
      this.regra.casos[ci].condicao.grupos[gi].condicoes.push(novaCondicao());
      this.emit();
    },
    removerCondicao(ci, gi, ki) {
      this.regra.casos[ci].condicao.grupos[gi].condicoes.splice(ki, 1);
      this.emit();
    },
    adicionarCondicaoSimples(ci) {
      this.regra.casos[ci].condicao.grupos[0].condicoes.push(novaCondicao());
      this.emit();
    },
    removerCondicaoSimples(ci, ki) {
      this.regra.casos[ci].condicao.grupos[0].condicoes.splice(ki, 1);
      this.emit();
    },
    atualizarEntao(ci, expr) {
      this.$set(this.regra.casos[ci], "entao", expr);
      this.emit();
    },
    atualizarSenao(expr) {
      this.$set(this.regra, "senao", expr);
      this.emit();
    },
  },
};
</script>

<style scoped>
.regra-condicional {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.header-info {
  font-size: 12px;
  color: #667085;
  background: #f9fafb;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px 12px;
}
.header-info .fa-icon, .header-info svg {
  color: #3a3e81;
  margin-right: 6px;
}
.regra-bloco {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
}
.regra-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.regra-titulo {
  font-weight: 600;
  font-size: 13px;
  color: #1d2939;
  flex: 0 0 auto;
}
.btn-modo-link {
  background: none;
  border: none;
  color: #3a3e81;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  flex: 1;
  text-align: left;
}
.btn-modo-link:hover {
  background: #f2f2f8;
}
.bloco-label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
}
.quando-bloco, .enviar-bloco {
  margin-top: 6px;
}
.enviar-bloco {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e9ecef;
}
.cond-lista {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cond-linha {
  display: flex;
  align-items: center;
  gap: 6px;
}
.cond-conector {
  font-size: 11px;
  font-weight: 600;
  color: #98a2b3;
  width: 24px;
  text-align: center;
}
.cond-campo { flex: 2; }
.cond-op { flex: 1.2; }
.cond-valor { flex: 1.5; }
.btn-add-inline {
  background: none;
  border: none;
  color: #3a3e81;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  align-self: flex-start;
  margin-top: 2px;
  transition: background 0.15s;
}
.btn-add-inline:hover {
  background: #f2f2f8;
}
.btn-add {
  background: #fff;
  border: 1px dashed #3a3e81;
  color: #3a3e81;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  align-self: flex-start;
  transition: all 0.15s;
}
.btn-add:hover {
  background: #f2f2f8;
}
.btn-mini {
  background: none;
  border: none;
  cursor: pointer;
  color: #98a2b3;
  padding: 2px 6px;
  border-radius: 4px;
}
.btn-mini--danger:hover {
  color: #d92d20;
  background: #fef3f2;
}
.modo-avancado {
  background: #fafbff;
  padding: 8px;
  border-radius: 6px;
  border: 1px dashed #dddce9;
}
.grupo-bloco {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px;
  margin-bottom: 8px;
}
.grupo-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 12px;
  color: #475467;
}
.grupo-prefix { font-weight: 500; }
.logica-select { max-width: 240px; }
.fallback-bloco {
  background: #fffaf0;
  border: 1px solid #fef3c7;
  border-radius: 8px;
  padding: 12px;
  margin-top: 4px;
}
.fallback-label {
  font-size: 11px;
  font-weight: 600;
  color: #b45309;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
}
</style>
